import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';

const UUID_PATTERN = /[a-f\d]{8}-([a-f\d]{4}-){3}[a-f\d]{12}/i;

interface RedirectUrlContextProps {
  redirectUrl: (nursingHomeId: string) => string;
  setRedirectUrl: React.Dispatch<
    React.SetStateAction<(nursingHomeId: string) => string>
  >;
}

const RedirectUrlContext = createContext<RedirectUrlContextProps | undefined>(
  undefined
);

export const useRedirectUrlContext = (): RedirectUrlContextProps => {
  const context = useContext(RedirectUrlContext);
  if (!context) {
    throw new Error('Missing RedirectUrlProvider!');
  }
  return context;
};

export const RedirectUrlProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { pathname } = useLocation();

  // Opened on a detail page (link, reload), there is no list page to go back
  // to yet, and the entity does not exist in the other nursing home.
  const [redirectUrl, setRedirectUrl] = useState<
    (nursingHomeId: string) => string
  >(
    () => () =>
      UUID_PATTERN.test(pathname) ? nhAppMainPages.DASHBOARD : pathname
  );

  useEffect(() => {
    if (!UUID_PATTERN.test(pathname)) {
      setRedirectUrl(() => () => pathname);
    }
  }, [pathname]);

  return (
    <RedirectUrlContext.Provider value={{ redirectUrl, setRedirectUrl }}>
      {children}
    </RedirectUrlContext.Provider>
  );
};
